import { Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators, AbstractControl } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ReplaySubject, takeUntil, firstValueFrom } from 'rxjs';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-modal';
import { ToastrService } from '../../services/toastr-service';
import { DamageCodeService } from '../../services/damage-code.service';
import { CreateDamageCodeRequest } from '../../models/create-damage-code-request.model';
import { DamageCodeResponse } from '../../models/damage-code-response.model';
import { ErrorComponent } from '../../shared-components/error/error.component';
import { DamageCodeFormValue } from '../../form-model/create-damage-code.form.model';
import { CreateDamageCodeHeaderComponent } from '../../components/create-damage-code/create-damage-code-header/create-damage-code-header.component';
import { MasterDataAuditLogComponent } from '../../shared-components/master-data-audit-log/master-data-audit-log.component';

@Component({
  selector: 'app-create-damage-code',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    ErrorComponent,
    NgxSkeletonLoaderModule,
    CreateDamageCodeHeaderComponent,
    MasterDataAuditLogComponent,
  ],
  templateUrl: './create-damage-code.component.html',
  styleUrls: ['./create-damage-code.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CreateDamageCodeComponent implements OnInit, OnDestroy {
  formSubmitErrors = signal<string[]>([]);
  submitLoader = signal<boolean>(false);
  isEditMode = signal<boolean>(false);
  showCancelDialog = signal<boolean>(false);
  openAuditLog = false;
  damageCode: string | null = null;
  damageTypeParam: number | null = null;
  damageCodeData = signal<DamageCodeResponse | null>(null);

  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  createDamageCodeForm = this.formBuilder.group({});

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly toastrService: ToastrService,
    private readonly damageCodeService: DamageCodeService,
  ) {}

  ngOnInit(): void {
    void this.initialize();
  }

  private async initialize(): Promise<void> {
    // Check if we're in edit mode by looking for damageCode and damageType in route params
    this.damageCode = this.route.snapshot.paramMap.get('damageCode');
    const dt = this.route.snapshot.paramMap.get('damageType');
    this.damageTypeParam = dt ? Number(dt) : null;

    // Always initialize form controls first
    this.addControlsToTheFormGroup();

    if (this.damageCode) {
      this.isEditMode.set(true);
      await this.loadDamageCodeData(this.damageCode, this.damageTypeParam ?? undefined);
    } else {
      this.isEditMode.set(false);
      // Ensure damageCode control is enabled for create mode
      this.createDamageCodeForm.get('damageCode')?.enable({ emitEvent: false });
    }
  }

  private async loadDamageCodeData(damageCode: string, damageType?: number): Promise<void> {
    try {
      const dtStr = damageType !== undefined && damageType !== null ? String(damageType) : undefined;
      const data = await firstValueFrom(this.damageCodeService.getDamageCodeByCode(damageCode, dtStr));
      this.damageCodeData.set(data);
      this.populateFormWithData(data);
      // Disable damageCode control in edit mode so code cannot be changed
      this.createDamageCodeForm.get('damageCode')?.disable({ emitEvent: false });
    } catch (err: any) {
      console.error('Error loading damage code data:', err);
      this.toastrService.showToastr('Failed to load damage code data', 'error');
      this.router.navigate(['/master-data/damage-code']);
    }
  }

  private populateFormWithData(data: DamageCodeResponse | null): void {
    if (!data) return;
    const extractDamageTypeCode = (val: any): string => {
      if (val === null || val === undefined) return '';
      if (typeof val === 'number') return String(val);
      const s = String(val);
      const m = /(\d+)/.exec(s);
      return m ? m[0] : s;
    };

    const damageTypeCode = extractDamageTypeCode(data.damageType);

    this.createDamageCodeForm.patchValue({
      damageCode: data.damageCode,
      description: data.description,
      damageType: damageTypeCode,
    });

    // Some web-component selects may not pick up the value immediately; re-set after a tick as a fallback.
    setTimeout(() => {
      const current = this.createDamageCodeForm.get('damageType')?.value;
      const desired = damageTypeCode;
      if (current !== desired) {
        const ctrl = this.createDamageCodeForm.get('damageType') as FormControl | null;
        if (ctrl) {
          ctrl.setValue(desired, { emitEvent: false });
        }
      }
    }, 0);
    // Mark form as pristine after initial load to track actual user changes
    this.createDamageCodeForm.markAsPristine();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private addControlsToTheFormGroup(): void {
    this.createDamageCodeForm.addControl(
      'damageCode',
      this.formBuilder.nonNullable.control<string>('', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(2),
        Validators.pattern(/^[a-zA-Z]{2}$/),
      ]),
    );
    this.createDamageCodeForm.addControl('damageType', this.formBuilder.nonNullable.control<string>('', [Validators.required]));
    this.createDamageCodeForm.addControl(
      'description',
      this.formBuilder.nonNullable.control<string>('', [Validators.required, Validators.maxLength(200)]),
    );
  }

  isControlInErrorState(control: FormControl | AbstractControl | null | undefined): boolean | null {
    return control?.invalid && control?.touched ? true : null;
  }

  onCancel(): void {
    if (this.createDamageCodeForm.dirty) {
      this.showCancelDialog.set(true);
    } else {
      this.navigateToDamageCode();
    }
  }

  confirmCancel(): void {
    this.showCancelDialog.set(false);
    this.navigateToDamageCode();
  }

  keepEditing(): void {
    this.showCancelDialog.set(false);
  }

  private navigateToDamageCode(): void {
    this.router.navigate(['/master-data/damage-code']);
  }

  onSubmit(): void {
    if (this.createDamageCodeForm.invalid) {
      for (const key of Object.keys(this.createDamageCodeForm.controls)) {
        this.createDamageCodeForm.get(key)?.markAsTouched();
      }
      this.toastrService.showToastr('Please fill in all required fields correctly', 'warning');
      return;
    }

    if (this.isEditMode()) {
      this.updateDamageCode();
    } else {
      this.createDamageCode();
    }
  }

  private buildDamageCodeRequest(): CreateDamageCodeRequest {
    const formValue = (
      this.isEditMode() ? this.createDamageCodeForm.getRawValue() : this.createDamageCodeForm.value
    ) as DamageCodeFormValue;
    return {
      damageCode: formValue.damageCode.toUpperCase(),
      description: formValue.description,
      damageType: Number(formValue.damageType),
    };
  }

  private createDamageCode(): void {
    this.submitLoader.set(true);
    this.formSubmitErrors.set([]);

    this.damageCodeService
      .createDamageCode(this.buildDamageCodeRequest())
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response: DamageCodeResponse) => {
          this.submitLoader.set(false);
          this.createDamageCodeForm.reset();
          // Ensure damageCode is enabled after create so form remains usable
          this.createDamageCodeForm.get('damageCode')?.enable({ emitEvent: false });
          this.formSubmitErrors.set([]);
          this.toastrService.showToastr(`Damage code '${response.damageCode}' created successfully`, 'success');
          this.router.navigate(['/master-data/damage-code']);
        },
        error: (error: HttpErrorResponse) => {
          this.handleDamageCodeError(error, 'creating');
        },
      });
  }

  private updateDamageCode(): void {
    this.submitLoader.set(true);
    this.formSubmitErrors.set([]);

    const req = this.buildDamageCodeRequest();
    // Use the original damageType from the loaded data to locate the record (API expects the original type in the path)
    const originalDamageTypeRaw = this.damageCodeData()?.damageType ?? req.damageType;
    const originalDamageTypeNumber = ((): number => {
      if (typeof originalDamageTypeRaw === 'number') return originalDamageTypeRaw;
      const m = /(\d+)/.exec(String(originalDamageTypeRaw));
      if (m) return Number.parseInt(m[0], 10);
      // fallback to numeric coercion
      return Number(originalDamageTypeRaw);
    })();
    // Build the update payload matching API contract: use `newDamageType` for the updated type
    const updatePayload = {
      description: req.description,
      newDamageType: req.damageType,
    };

    this.damageCodeService
      .updateDamageCode(this.damageCode!, originalDamageTypeNumber, updatePayload)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response: DamageCodeResponse) => {
          this.submitLoader.set(false);
          this.formSubmitErrors.set([]);
          this.toastrService.showToastr(`Damage code '${response.damageCode}' updated successfully`, 'success');
          this.router.navigate(['/master-data/damage-code']);
        },
        error: (error: HttpErrorResponse) => {
          this.handleDamageCodeError(error, 'updating');
        },
      });
  }

  private handleDamageCodeError(error: HttpErrorResponse, operation: string): void {
    this.submitLoader.set(false);
    console.error(`Error ${operation} damage code:`, error);

    if (error.status === 400) {
      this.formSubmitErrors.set(['Invalid damage code data. Please check your inputs.']);
      this.toastrService.showToastr('Invalid damage code data. Please check your inputs.', 'error');
    } else if (error.status === 409) {
      this.formSubmitErrors.set(['Damage code already exists. Please use a different code.']);
      this.toastrService.showToastr('Damage code already exists.', 'error');
    } else if (error.status === 422 && error.error?.violations) {
      const errors = error.error.violations.map((v: { message: string }) => v.message);
      this.formSubmitErrors.set(errors);
      this.toastrService.showToastr('Validation errors occurred. Please check the form.', 'error');
    } else {
      this.formSubmitErrors.set([`Failed to ${operation} damage code. Please try again.`]);
      this.toastrService.showToastr(`Failed to ${operation} damage code. Please try again.`, 'error');
    }
  }

  onAuditLogClick(): void {
    if (!this.damageCode || this.damageTypeParam === null) {
      this.toastrService.showToastr('Damage code not available for audit log', 'error');
      return;
    }
    this.openAuditLog = true;
  }

  readonly auditLogFetcher = (_transactionCode: string, isSystemLog: boolean, page: number, limit: number) =>
    this.damageCodeService.getDamageCodeAuditLogs(this.damageCode!, this.damageTypeParam!, isSystemLog, page, limit);
}
